<template>
  <div>
    <div class="evollu-form" v-if="showFilters">

      <div class="form-row">
        <div class="col-12 col-md-4">
          <div>
            <label class="form-label">{{ $t('evocall.requests.date_range') }}:</label>
            <RangeDatePicker
              id="selected-time-date-picker"
              v-model="filters.dateRange"
              :placeholder="$t('evocall.requests.date_range')"
              :clearable="false"
            />
          </div>
        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">
            {{ $t('evocall.requests.accepted_by') }}
          </label>
          <multiselect
            id="acceptees-filter"
            v-model="filters.acceptees"
            :options="accepteesOptions"
            track-by="id"
            :customLabel="formatedUserLabel"
            :placeholder="accepteesOptions.length ? $t('clinical.waiting_room_statistics.all_collaborators') : $t('clinical.waiting_room_statistics.no_collaborators')"
            :allow-empty="true"
            :multiple="true"
            :disabled="!accepteesOptions.length"
            :close-on-select="false"
          >

            <template slot="clear">
              <span class="multiselect__clear" v-if="filters.acceptees.length > 0"
                    @mousedown.prevent.stop="filters.acceptees = []"></span>
            </template>
          </multiselect>
        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">
            {{ $t('evocall.requests.creator') }}
          </label>
          <multiselect
            id="creators-filter"
            v-model="filters.creators"
            :options="creatorsOptions"
            track-by="id"
            :customLabel="formatedUserLabel"
            :placeholder="creatorsOptions.length ? $t('clinical.waiting_room_statistics.all_collaborators') : $t('clinical.waiting_room_statistics.no_collaborators')"
            :allow-empty="true"
            :multiple="true"
            :disabled="!creatorsOptions.length"
            :close-on-select="false"
          >

            <template slot="clear">
              <span class="multiselect__clear" v-if="filters.creators.length > 0"
                    @mousedown.prevent.stop="filters.creators = []"></span>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="form-row">
        <div class="col-12 col-md-4">
          <label class="form-label">
            {{ $t('clinical.call_ratings.audiologist_rating') }}
          </label>
          <div class="row">
            <va-rating class="ml-3 mt-1"
              :readonly="false"
                       v-model="filters.audiologistRating"
                       size="medium"
                       color="info"/>

            <span v-show="filters.audiologistRating !== null" class="clickable">
              <i class="fa fa-times-circle ml-2" style="color:darkgray" @click="clearAudiologistFilterRating"/>
            </span>
          </div>

        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">
            {{ $t('clinical.call_ratings.dispenser_rating') }}
          </label>
          <div class="row">
            <va-rating class="ml-3 mt-1"
                       :readonly="false"
                       v-model="filters.dispenserRating"
                       size="medium"
                       color="info"/>

            <span v-show="filters.dispenserRating !== null" class="clickable">
              <i class="fa fa-times-circle ml-2" style="color:darkgray" @click="clearDispenserFilterRating"/>
            </span>
          </div>

        </div>
      </div>
    </div>

    <v-server-table
      :columns="columns"
      :options="tableOptions"
      @filter="onFilterUpdate"
      @row-click="onRowClicked"
      id="callRatingsTable"
      ref="callRatingsTable"
      name="callRatingsTable"
    >
    <span v-if="row.created_at" slot="created_at" slot-scope="{row}">
      {{ row.created_at | localTime('YYYY-MM-DD HH:mm') }}
    </span>

      <span v-if="row.waiting_time" slot="waiting_time" slot-scope="{row}">
      {{ getRelativeTimeString(row.waiting_time) }}
    </span>

      <span slot="appointment_duration" slot-scope="{row}">
      <span v-if="row.ended_at === null">
        {{ $t('clinical.appointments.ongoing') }}
      </span>
      <span v-else-if="row.started_at && row.ended_at">
        {{ getRelativeTimeString(getDurationTime(row)) }}
      </span>
      <span v-else>
        ({{ row.estimated_duration }} {{ $t('time.minutes') }})
      </span>
    </span>

      <span slot="audiologist" slot-scope="{row}">
      {{ getAudiologistName(row) || $t('clinical.appointments.undefined_user') }}
    </span>

      <span slot="dispenser" slot-scope="{row}">
      {{ getDispenserName(row) || $t('clinical.appointments.undefined_user') }}
    </span>

      <div class="d-flex justify-content-center" slot="audiologist_rating" slot-scope="{row}">
        <va-rating v-if="row.audiologist_rating"
                   :readonly="true"
                   v-model="row.audiologist_rating"
                   size="medium"
                   color="info"/>
        <span v-else> N/A </span>
      </div>

      <div class="d-flex justify-content-center" slot="dispenser_rating" slot-scope="{row}">
        <va-rating v-if="row.dispenser_rating"
                   v-model="row.dispenser_rating"
                   :readonly="true"
                   size="medium"
                   color="info"/>
        <span v-else> N/A </span>
      </div>

      <span slot="obs" class="no-break observations__row" slot-scope="{row}">
      <div v-if="row.obs">
        <va-popover
          :message="row.obs"
          placement="left"
        >
          <i class="i-has-comment-gray"></i>
        </va-popover>
      </div>
    </span>

      <span slot="h__comments"></span>
      <span v-show="row.dispenser_comment !== null || row.audiologist_comment !== null" slot="comments" slot-scope="{ row }">
          <i class="i-has-comment-gray"/>
        </span>
    </v-server-table>
  </div>
</template>

<script>
import _, { uniqBy } from 'lodash'
import Multiselect from 'vue-multiselect'
import TableQueryMixin from '@/mixins/TableQueryMixin'
import RelativeTimeMixin from '@/mixins/RelativeTimeMixin'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import { ActivityService } from '@/api/AuthService'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import { mapState } from 'vuex'

const CALL_RATINGS_INFO_TYPE = 550

export default {
  props: {
    service: {
      type: Object,
      required: true
    },
    showFilters: {
      type: Boolean,
      required: false,
    },
    querySearch: {
      type: String,
      required: false,
      default: null
    },
    customQueries: {
      type: Object,
      required: false,
      default: null
    }
  },
  components: {
    Multiselect,
    RangeDatePicker
  },
  mixins: [
    TableQueryMixin,
    RelativeTimeMixin,
    MultiselectLabelFormatter
  ],
  created: function () {
    this.loadEntityActivityUsersForPeriod(this.filters.dateRange.start, this.filters.dateRange.end)
      .then(() => {
        this.loadAccepteesAndCreators()
      })
      .finally(() => { this.reloadTable() })
  },
  data () {
    return {
      columns: [
        'created_at',
        'person.name',
        'facility.name',
        'appointment_duration',
        'audiologist',
        'dispenser',
        'audiologist_rating',
        'dispenser_rating',
        'comments'
      ],
      queryUrl: `${this.service.url}/admin/appointments/rates`,
      tableOptions: {
        columnsDropdown: false,
        saveState: true,
        filterable: true,
        customFilters: ['start', 'end', 'acceptees', 'creators', 'audiologistRating', 'dispenserRating'],
        sortable: ['created_at', 'dispenser_rating', 'audiologist_rating'],
        headings: {
          'created_at': this.$t('evocall.requests.created_at'),
          'person.name': this.$t('evocall.requests.person_name'),
          'facility.name': this.$t('evocall.requests.facility'),
          'appointment_duration': this.$t('evocall.history.appointment_duration'),
          'audiologist': this.$t('evocall.requests.accepted_by'),
          'dispenser': this.$t('evocall.requests.creator'),
          'audiologist_rating': this.$t('clinical.call_ratings.audiologist_rating'),
          'dispenser_rating': this.$t('clinical.call_ratings.dispenser_rating'),
        },
        columnsClasses: {
          audiologist_rating: 'content-center',
          dispenser_rating: 'content-center',
          comments: 'content-center'
        },
        orderBy: {
          column: 'created_at',
          ascending: false
        }
      },
      filters: {
        dateRange: {
          start: this.moment().subtract(1, 'months').format('YYYY-MM-DD'),
          end: this.moment().format('YYYY-MM-DD'),
        },
        acceptees: [],
        creators: [],
        audiologistRating: null,
        dispenserRating: null
      },
      entityActivityUsersForPeriod: []
    }
  },
  methods: {
    clearAudiologistFilterRating: function () {
      this.filters.audiologistRating = null
    },
    clearDispenserFilterRating: function () {
      this.filters.dispenserRating = null
    },
    reloadTable: function () {
      const tableView = this.getTableReference()
      if (tableView) {
        tableView.refresh()
      }
    },
    loadAccepteesAndCreators: function () {
      return new Promise((resolve) => {
        if (this.customQueries !== null) {
          if (this.customQueries.hasOwnProperty('acceptees') && typeof this.customQueries.acceptees === 'string') {
            try {
              const accepteeIds = this.customQueries.acceptees.split(',').map(id => parseInt(id))
              this.filters.acceptees = this.accepteesOptions.filter(acceptee => accepteeIds.includes(acceptee.id))
            } catch (errors) {
              console.log(errors)
            }
          }

          if (this.customQueries.hasOwnProperty('creators') && typeof this.customQueries.creators === 'string') {
            try {
              const creatorIds = this.customQueries.creators.split(',').map(id => parseInt(id))
              this.filters.creators = this.creatorsOptions.filter(creator => creatorIds.includes(creator.id))
            } catch (errors) {
              console.log(errors)
            }
          }
        }
        resolve()
      })
    },
    getTableReference: function () {
      return this.$refs.callRatingsTable
    },
    loadEntityActivityUsersForPeriod: function (start, end) {
      return new Promise((resolve, reject) => {
        if (start && end) {
          ActivityService.getActivityUsersByEntity({
            accessType: this.accessTypes.READ,
            serviceId: this.service.id,
            infoType: CALL_RATINGS_INFO_TYPE,
            start: this.moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            end: this.moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            activities: `${this.activities.AUDIOLOGY},${this.activities.DISPENSER_FIELD},${this.activities.DISPENSER_PREMISES}`
          })
            .then(entityActivityRecords => {
              this.entityActivityUsersForPeriod = entityActivityRecords
              resolve()
            }).catch(errors => {
              console.log(errors)
              reject(errors)
            })
        } else {
          reject(new Error('Load Entity Activity Users For Period - Invalid args'))
        }
      })
    },
    getDurationTime: function (call) {
      let start = this.moment(call.started_at)
      let end = this.moment(call.ended_at)
      return end.diff(start, 'second')
    },
    onRowClicked: function ({ row }) {
      if (row.ended_at !== null) {
        this.$emit('row-clicked', { callRequestAppointment: row })
      } else {
        this.showToast(this.$t('evocall.history.request_has_not_finished'), this.$store.state.errorToastOptions)
      }
    },
    getDispenserName: function (appointmentCallRequest) {
      const dispenser = _.get(appointmentCallRequest, 'parent.user', null)
      return dispenser ? dispenser.nickname || dispenser.name : null
    },
    getAudiologistName: function (appointmentCallRequest) {
      const audiologist = _.get(appointmentCallRequest, 'user', null)
      return audiologist ? audiologist.nickname || audiologist.name : null
    },
  },
  computed: {
    ...mapState('auth', [
      'activities',
      'accessTypes'
    ]),
    activeFilters: function () {
      return Object.keys(this.filters).reduce((accumulator, currentValue) => accumulator + (Array.isArray(this.filters[currentValue]) ? (this.filters[currentValue].length > 0 ? 1 : 0) : typeof this.filters[currentValue] === 'object' ? Object.values(this.filters[currentValue]).some(value => value) ? 1 : 0 : this.filters[currentValue] !== '' ? 1 : 0), 0)
    },
    accepteesOptions: function () {
      return uniqBy(this.entityActivityUsersForPeriod
        .filter(entityActivityUser => entityActivityUser.activity.id === this.activities.AUDIOLOGY && entityActivityUser.hasOwnProperty('user') && entityActivityUser.user !== null)
        .map(entityActivityUser => entityActivityUser.user), 'id')
    },
    creatorsOptions: function () {
      const dispenserActivities = [this.activities.DISPENSER_FIELD, this.activities.DISPENSER_PREMISES]
      return uniqBy(this.entityActivityUsersForPeriod
        .filter(entityActivityUser => dispenserActivities.includes(entityActivityUser.activity.id) && entityActivityUser.hasOwnProperty('user') && entityActivityUser.user !== null)
        .map(entityActivityUser => entityActivityUser.user), 'id')
    }
  },
  watch: {
    querySearch: {
      handler: function (value) {
        if (this.$refs.hasOwnProperty('callRatingsTable')) {
          this.$refs.callRatingsTable.setFilter(value)
        }
      },
      immediate: true
    },
    customQueries: {
      handler: function (value) {
        if (value.audiologistRating && value.audiologistRating !== '') {
          this.filters.audiologistRating = value.audiologistRating
        }
        if (value.dispenserRating && value.dispenserRating !== '') {
          this.filters.dispenserRating = value.dispenserRating
        }
        if (value.start && value.start !== '') {
          this.filters.dateRange.start = value.start
        } else {
          this.$nextTick(function () {
            this.$refs.callRatingsTable.customQueries.start = this.moment().subtract(1, 'months').format('YYYY-MM-DD')
          })
        }

        if (value.end && value.end !== '') {
          this.filters.dateRange.end = value.end
        } else {
          this.$nextTick(function () {
            this.$refs.callRatingsTable.customQueries.end = this.moment().format('YYYY-MM-DD')
          })
        }
      },
      immediate: true
    },
    'filters.dateRange': function (value) {
      this.$refs.callRatingsTable.customQueries.start = value.start || ''
      this.$refs.callRatingsTable.customQueries.end = value.end || ''
      this.emitTableFilter('vue-tables.callRatingsTable.filter::end', value.end || '')
    },
    'filters.acceptees': function (acceptees) {
      if (Array.isArray(acceptees)) {
        this.emitTableFilter('vue-tables.callRatingsTable.filter::acceptees', acceptees.map(acceptee => acceptee.id).join(','))
      }
    },
    'filters.creators': function (creators) {
      if (Array.isArray(creators)) {
        this.emitTableFilter('vue-tables.callRatingsTable.filter::creators', creators.map(creator => creator.id).join(','))
      }
    },
    'filters.audiologistRating': function (audRating) {
      this.emitTableFilter('vue-tables.callRatingsTable.filter::audiologistRating', audRating)
    },
    'filters.dispenserRating': function (dispRating) {
      this.emitTableFilter('vue-tables.callRatingsTable.filter::dispenserRating', dispRating)
    },
    activeFilters: {
      handler: function (activeFilters) {
        this.$emit('filters-updated', activeFilters)
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.content-center {
  text-align: center;
}

#callRatingsTable {
  $assetsPath: "../../../assets";
  @import '@/sass/icons/icons-classes.scss';
  $primary-green: #29C4A9;

  i {
    display: block;
    width: 1.5rem;
    height: 1.5rem;
  }

  tr:hover {
    .va-rating-item__icon {
      color: white !important;
      opacity: 1 !important;
    }

    .va-rating__icon-item--empty {
      opacity: .3 !important;
    }
  }

  tr {
    .va-rating-item__icon {
      color: $primary-green !important;
      opacity: 1 !important;
    }

    .va-rating__icon-item--empty {
      opacity: .3 !important;
    }
  }

  .i-has-comment-gray {
    @extend .i-has-comment-gray;
  }
}
</style>
